import { Injectable } from "@angular/core";
import { environment as env } from "src/environments/environment";
import { MixPanelConfigObject } from "../model/mix-panel-config.model";
import { MixpanelEvent } from "../model/mix-panel-event.enum";
import { NavigationEnd, NavigationStart, NavigationError, Router } from "@angular/router";
import { filter } from "rxjs/operators";

// Declare global mixpanel from CDN script
declare global {
  var mixpanel: any;
}

@Injectable({
    providedIn:'root'
})
export class MixpanelService {

  constructor(private router: Router) {
    // Verify that global mixpanel === window.mixpanel (they are the same object)
    setTimeout(() => {
      console.log('mixpanel === window.mixpanel:', mixpanel === (window as any).mixpanel);
    }, 1000);
    
    // Mixpanel is already initialized in index.html
    // Wait a moment for Angular to fully bootstrap before tracking
    // setTimeout(() => {
    //   // Capture initial URL parameters when service is initialized
    //   const initialUrlParams = this.extractUrlParameters(window.location.href);
      
      // this.trackEvent({
      //   event: MixpanelEvent.SESSION_STARTED,
      //   properties: {
      //     ...initialUrlParams
      //   }
      // });
      
      // this.traceUser();
    // }, 500);
  }

  trackEvent(event: MixPanelConfigObject) {
    if (typeof mixpanel !== 'undefined' && mixpanel && typeof mixpanel.track === 'function') {
      try {
        mixpanel.track(event.event, event.properties);
      } catch (error) {
        console.warn('Mixpanel tracking failed:', error);
      }
    }
  }

  identifyUser(userId: string) {
    if (typeof mixpanel !== 'undefined' && mixpanel && typeof mixpanel.identify === 'function') {
      try {
        mixpanel.identify(userId);
      } catch (error) {
        console.warn('Mixpanel identify failed:', error);
      }
    }
  }

  alias(userId: string) {
    if (typeof mixpanel !== 'undefined' && mixpanel && typeof mixpanel.alias === 'function') {
      try {
        mixpanel.alias(userId);
      } catch (error) {
        console.warn('Mixpanel alias failed:', error);
      }
    }
  }

  setUserProperties(properties: any) {
    if (typeof mixpanel !== 'undefined' && mixpanel && mixpanel.people && typeof mixpanel.people.set === 'function') {
      try {
        mixpanel.people.set(properties);
      } catch (error) {
        console.warn('Mixpanel set properties failed:', error);
      }
    }
  }

  traceUser() {

    // Track navigation attempts (when user starts navigating)
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe((event: NavigationStart) => {
      const urlParams = this.extractUrlParameters(event.url);
      
      this.trackEvent({
        event: MixpanelEvent.NAVIGATION_STARTED, // or create a new event like NAVIGATION_STARTED
        properties: {
          event_type: 'navigation_intent',
          navigation_trigger: event.navigationTrigger || 'unknown',
          target_url: event.url,
          ...urlParams
        }
      })
      console.log('Navigation started to:', event.url);
    });

    // Track successful page views (when navigation completes)
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const urlParams = this.extractUrlParameters(event.urlAfterRedirects);
      
      this.trackEvent({
        event: MixpanelEvent.PAGE_VIEWED,
        properties: {
          title: 'page_view_completed',
          customeId: event.urlAfterRedirects.slice(1) || 'home',
          feature_context: event.urlAfterRedirects,
          ...urlParams
        }
      })
      console.log('Page viewed:', event.urlAfterRedirects.slice(1));
    });

    // Optional: Track failed navigations
    this.router.events.pipe(
      filter(event => event instanceof NavigationError)
    ).subscribe((event: NavigationError) => {
      console.log('Navigation failed:', event.url, 'Error:', event.error);
      const urlParams = this.extractUrlParameters(event.url);
      
      this.trackEvent({
        event: MixpanelEvent.NAVIGATION_FAILED, // or create NAVIGATION_ERROR event
        properties: {
          event_type: 'navigation_failed',
          failed_url: event.url,
          ...urlParams
        }
      })
    });

  }

  /**
   * Detect traffic source from URL parameters
   */
  private detectTrafficSource(params: URLSearchParams): string {
    const fbclid = params.get('fbclid');
    const gclid = params.get('gclid') || params.get('gbraid');
    
    if (fbclid) {
      return 'facebook';
    } else if (gclid) {
      return 'google';
    } else if (params.get('utm_source')) {
      // Fallback to utm_source if click IDs not present
      const utmSource = params.get('utm_source')?.toLowerCase();
      if (utmSource?.includes('fb') || utmSource?.includes('facebook')) {
        return 'facebook';
      } else if (utmSource?.includes('google') || utmSource?.includes('adwords')) {
        return 'google';
      }
    }
    
    return '';
  }

  /**
   * Extract platform-specific click ID
   */
  private extractClickId(params: URLSearchParams, trafficSource: string): string | null {
    if (trafficSource === 'facebook') {
      return params.get('fbclid');
    } else if (trafficSource === 'google') {
      return params.get('gclid') || params.get('gbraid');
    }
    return null;
  }

  /**
   * Extract URL parameters including UTM parameters and ad platform specific parameters
   * Normalize Facebook and Google parameters to common keys
   */
  private extractUrlParameters(url: string): any {
    try {
      // Handle relative URLs by converting to absolute URLs
      let absoluteUrl: string;
      if (url.startsWith('http')) {
        absoluteUrl = url;
      } else {
        // Convert relative URL to absolute URL
        absoluteUrl = window.location.origin + (url.startsWith('/') ? url : '/' + url);
      }
      
      const urlObj = new URL(absoluteUrl);
      const params = new URLSearchParams(urlObj.search);
      const extractedParams: any = {};

      // Step 1: Detect traffic source
      const trafficSource = this.detectTrafficSource(params);
      
      // Only process if we have Facebook or Google traffic
      if (!trafficSource) {
        return extractedParams;
      }

      // Step 2: Set ad source
      extractedParams.ad_source = trafficSource;

      // Step 3: Extract click ID
      const clickId = this.extractClickId(params, trafficSource);
      if (clickId) {
        extractedParams.click_id = clickId;
      }

      // Step 4: Extract common UTM parameters (used by both Google and Facebook)
      if (params.get('utm_campaign')) extractedParams.campaign_name = params.get('utm_campaign');
      if (params.get('utm_medium')) extractedParams.medium = params.get('utm_medium');
      if (params.get('utm_source')) extractedParams.source = params.get('utm_source');
      if (params.get('utm_content')) extractedParams.creative_id = params.get('utm_content');
      
      // Step 5: Extract platform-specific parameters
      if (trafficSource === 'google') {
        if (params.get('utm_id')) extractedParams.campaign_id = params.get('utm_id');
        if (params.get('utm_term')) extractedParams.keyword = params.get('utm_term');
      } else if (trafficSource === 'facebook') {
        // Facebook may use utm_id but it's less common, still capture if present
        if (params.get('utm_id')) extractedParams.campaign_id = params.get('utm_id');
        // Facebook doesn't typically use utm_term, but capture if present
        if (params.get('utm_term')) extractedParams.keyword = params.get('utm_term');
      }

      // Step 6: Add metadata
      extractedParams.has_ad_parameters = true;
      extractedParams.parameter_count = Object.keys(extractedParams).length - 2; // Exclude metadata fields

      return extractedParams;
    } catch (error) {
      console.warn('Failed to extract URL parameters from:', url, error);
      return {};
    }
  }
}
